import { Injectable } from '@angular/core';
import { FormControlProvider } from '../formbuilder/formcontrol';
import { IFormControl } from '../formbuilder/formcontrol.interface';
import { RouteProvider } from '../route/route';
import { LoadingProvider } from '../loading/loading';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { IOrder } from '../../model/models/order/order-interface';
import { DeliveryMethodType } from '../../model/types/delivery-method-type';
import { PixIdTypeValue, VPA_FORM_VERSION_SINGLE_FIELD, VirtualPaymentType } from '../../model/types/vpa-type';

export const VPA_FORM_DELIVERY_METHODS: DeliveryMethodType[] = [
  DeliveryMethodType.UPI,
  DeliveryMethodType.Alipay,
  DeliveryMethodType.PIX,
  DeliveryMethodType.BreB,
];

@Injectable()
export class VpaFormProvider {

  constructor(private formControlProvider: FormControlProvider,
              private routeProvider: RouteProvider,
              private loadingProvider: LoadingProvider) {
  }

  isVpaFormDeliveryMethod(deliveryMethod: DeliveryMethodType): boolean {
    return VPA_FORM_DELIVERY_METHODS.indexOf(deliveryMethod) !== -1;
  }

  async getRecipientFields(order: IOrder, deliveryMethod: DeliveryMethodType = order.deliveryMethodInternal): Promise<IFormControl[]> {
    const params = {
      amountFrom: order.amountFrom,
      deliveryMethodID: VirtualPaymentType[deliveryMethod],
      countryTo: order.countryTo,
      currencyTo: order.currencyTo
    };
    if (deliveryMethod === DeliveryMethodType.PIX) {
      params['PIXIDType'] = PixIdTypeValue.CPFNumber;
    }
    if (deliveryMethod === DeliveryMethodType.BreB) {
      params['vpaFormVersion'] = VPA_FORM_VERSION_SINGLE_FIELD;
    }
    return this.formControlProvider.getFields({
      uri: 'recipient/bank-deposit',
      params
    });
  }

  applyFormData(order: IOrder, deliveryMethod: DeliveryMethodType, data) {
    order.virtualPaymentType = VirtualPaymentType[deliveryMethod];
    switch (deliveryMethod) {
      case DeliveryMethodType.UPI:
        order.virtualPaymentAccountNo = data.uPI;
        order.bankName = data.uPIBankName;
        break;
      case DeliveryMethodType.Alipay:
        order.virtualPaymentAccountNo = data.alipay;
        break;
      case DeliveryMethodType.PIX:
        order.virtualPaymentAccountNo = data.pIX;
        break;
      case DeliveryMethodType.BreB:
        order.virtualPaymentAccountNo = data.breB;
        break;
      default:
        break;
    }
  }

  /**
   * Opens VpaFormPage so the customer re-enters the payout key of the order's recipient.
   * Resolves false (without navigating) when the delivery method has no VPA form, its fields
   * cannot be loaded or the navigation is skipped; otherwise resolves true once the page is pushed.
   * `onSaved` runs after the page saved the key and the order was updated; `onBack` replaces the
   * default back navigation.
   */
  async openForMissingKey(order: IOrder, onSaved: (order: IOrder) => void, onBack: () => void): Promise<boolean> {
    const deliveryMethod = order && order.deliveryMethodInternal;
    if (!order || !order.recipient || !this.isVpaFormDeliveryMethod(deliveryMethod))
      return false;

    this.loadingProvider.show();
    let recipientFields: IFormControl[] = null;
    try {
      recipientFields = await this.getRecipientFields(order, deliveryMethod);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to load VPA fields for missing payout key');
    }
    this.loadingProvider.hide();
    if (!recipientFields)
      return false;

    const transition = this.routeProvider.push(this.routeProvider.pages.VpaFormPage, {
      recipientFields,
      recipient: order.recipient,
      order,
      onContinue: (data) => {
        this.applyFormData(order, deliveryMethod, data);
        onSaved(order);
      },
      onBackCallback: onBack,
    });
    if (!transition)
      return false;
    await transition;
    return true;
  }
}



// WEBPACK FOOTER //
// ./src/providers/vpa-form/vpa-form.ts